import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Sparkles, RefreshCw, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { api } from '../context/AuthContext';
import { toast } from 'sonner';

const COOLDOWN_SECONDS = 300;

function formatCountdown(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function VerifyEmailSentPage() {
  const { user } = useAuth();
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(COOLDOWN_SECONDS);
  const timerRef = useRef(null);

  const startCountdown = (from = COOLDOWN_SECONDS) => {
    clearInterval(timerRef.current);
    setCountdown(from);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    startCountdown();
    return () => clearInterval(timerRef.current);
  }, []);

  const handleResend = async () => {
    setResending(true);
    try {
      await api.post('/auth/send-verification-email');
      toast.success('Verification email sent! Check your inbox.');
      startCountdown();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900 flex items-center justify-center p-6">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl">GDS</span>
          </Link>
        </div>

        <Card className="border-0 shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center mx-auto">
              <Mail className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
            </div>

            <h2 className="text-xl font-semibold">Check your inbox</h2>

            <p className="text-slate-600 dark:text-slate-400 text-sm">
              We sent a verification link to{' '}
              {user?.email ? (
                <span className="font-medium">{user.email}</span>
              ) : (
                'your email address'
              )}
              . Click the link to verify your account and continue.
            </p>

            <p className="text-slate-500 text-xs">The link expires in 24 hours.</p>

            <Button
              onClick={handleResend}
              disabled={resending || countdown > 0}
              variant="outline"
              className="w-full mt-2"
            >
              {resending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {resending
                ? 'Sending…'
                : countdown > 0
                ? `Resend in ${formatCountdown(countdown)}`
                : 'Resend verification email'}
            </Button>

            <p className="text-slate-500 dark:text-slate-600 text-xs pt-2">
              Wrong account?{' '}
              <Link to="/login" className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500">
                Sign in with a different account
              </Link>
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
